import { getInternalServerConfig, getAuthToken, resolveServerConfig } from './auth';

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

const getConfig = async () => {
  const internalServer = getInternalServerConfig();
  const baseUrl = internalServer.baseUrl;

  if (!baseUrl) {
    throw new ApiError('No API URL configured. Please configure in settings.', 500);
  }

  const token = await getAuthToken(internalServer);

  return {
    baseUrl,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/ld+json'
    }
  };
};

export const getLogisticsObjects = async (type = '') => {
  const config = await getConfig();

  const response = await fetch(`${config.baseUrl}/logistics-objects/internal/_all?limit=2000&offset=0&t=${type}`, {
    method: 'GET',
    headers: {
      ...config.headers
    }
  });

  if (!response.ok) {
    throw new ApiError('Failed to fetch logistics objects', response.status);
  }

  return response.json();
};

export const apiCall = async (endpoint, options = {}) => {
  const config = await getConfig();

  const response = await fetch(`${config.baseUrl}${endpoint}`, {
    ...options,
    headers: {
      ...config.headers,
      ...options.headers
    }
  });

  if (!response.ok) {
    throw new ApiError('API request failed', response.status);
  }

  // Don't try to parse JSON for PATCH requests or 204 responses
  if (options.method === 'PATCH' || response.status === 204 || response.status === 201) {
    return response;
  }

  return response.json();
};

export const getLogisticsObjectWithRevision = async (objectId) => {
  const config = await getConfig();

  const response = await fetch(`${config.baseUrl}/logistics-objects/${objectId}`, {
    method: 'GET',
    headers: {
      ...config.headers,
      'Accept': 'application/ld+json'
    }
  });

  if (!response.ok) {
    throw new ApiError('Failed to fetch logistics object', response.status);
  }

  const latestRevision = response.headers.get('Latest-Revision');
  const data = await response.json();

  return {
    data,
    revision: latestRevision ? parseInt(latestRevision, 10) : 0
  };
};

export const submitChangeRequest = async (objectId, objectType, operations, revision) => {
  const config = await getConfig();

  const changeRequest = {
    "@context": {
      "cargo": "https://onerecord.iata.org/ns/cargo#",
      "api": "https://onerecord.iata.org/ns/api#"
    },
    "@type": "api:Change",
    "api:hasLogisticsObject": {
      "@id": objectId
    },
    "api:hasDescription": `Update ${objectType}`,
    "api:hasOperation": operations,
    "api:hasRevision": {
      "@type": "http://www.w3.org/2001/XMLSchema#positiveInteger",
      "@value": (revision + 1).toString()
    }
  };

  const response = await fetch(`${config.baseUrl}/change-requests`, {
    method: 'POST',
    headers: {
      ...config.headers,
      'Content-Type': 'application/ld+json'
    },
    body: JSON.stringify(changeRequest)
  });

  if (!response.ok) {
    throw new ApiError('Failed to submit change request', response.status);
  }

  return response;
};

export const externalApiCall = async (baseUrl, endpoint, options = {}) => {
  const {
    server,
    returnFullResponse,
    headers: customHeaders = {},
    ...fetchOptions
  } = options;

  const resolvedServer = resolveServerConfig(server || { baseUrl });
  const token = await getAuthToken(resolvedServer);

  const response = await fetch(`${baseUrl}${endpoint}`, {
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/ld+json',
      Authorization: `Bearer ${token}`,
      ...customHeaders
    }
  });

  if (!response.ok) {
    throw new ApiError('API request failed', response.status);
  }

  if (returnFullResponse) {
    return response;
  }

  // Don't try to parse JSON for PATCH requests or 204 responses
  if (fetchOptions.method === 'PATCH' || response.status === 204 || response.status === 201) {
    return response;
  }

  return response.json();
};
